import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import SchedulerScrollButtons from '../../Scheduler/feature/ScrollButtons.js';
import DomHelper from '../../Core/helper/DomHelper.js';
import DomSync from '../../Core/helper/DomSync.js';
import Objects from '../../Core/helper/util/Objects.js';

/**
 * @module Gantt/feature/ScrollButtons
 */

/**
 *
 * This feature injects buttons in each row that scrolls the task bar into view. It can optionally show a label along with the
 * button, using the {@link #config-labelRenderer}.
 *
 * ```javascript
 * new Gantt({
 *     appendTo          : 'container',
 *     features : {
 *         scrollButtons : {
 *             labelRenderer({ taskRecord }) {
 *                 return `${taskRecord.wbsCode} ${StringHelper.encodeHtml(taskRecord.name)}`;
 *             }
 *         }
 *     }
 * ```
 *
 * This feature is **disabled** by default.
 *
 * {@inlineexample Gantt/feature/ScrollButtons.js}
 *
 * @demo Gantt/scroll-buttons
 *
 * @extends Scheduler/feature/ScrollButtons
 * @feature
 * @classtype scrollButtons
 *
 * @typings Scheduler.feature.ScrollButtons -> Scheduler.feature.SchedulerScrollButtons
 */
export default class ScrollButtons extends SchedulerScrollButtons {

    static $name = 'ScrollButtons';

    refreshScrollButtons() {
        const
            me = this,
            { client, rowStyleMap } = me;

        // In case of infinite scroll, we don't immediately have visibleDateRange,
        // which we need in generateScrollButton
        // so we wait until the next time we get rowManager.renderDone event
        if (!client.visibleDateRange) {
            return;
        }

        if (!me.scrollButtonsCanvas) {
            me.scrollButtonsCanvas = DomHelper.createElement({
                className     : 'b-scrollbuttons-canvas',
                parent        : client.timeAxisSubGridElement,
                retainElement : true
            });
        }

        const { taskStore } = client;

        DomSync.sync({
            targetElement : me.scrollButtonsCanvas,
            domConfig     : {
                // Create Scroll Buttons only for visible rows
                children : client.rowManager.rows.map(row => {
                    const
                        rowIndex = row.dataIndex,
                        { height, transform } = rowStyleMap.get(rowIndex),
                        taskRecord = taskStore.getById(row.id);

                    return taskRecord ? {
                        class   : 'b-scroll-buttons-container',
                        dataset : {
                            taskId : taskRecord.id,
                            rowIndex
                        },
                        style : {
                            // Copy styles from row so we stay in sync with the row
                            height,
                            transform
                        },
                        children : me.generateScrollButton(taskRecord)
                    } : null;
                })
            }
        });
    }

    generateScrollButton(record) {
        if (!record.isScheduled) {
            return null;
        }

        const
            { labelRenderer, backwardIconCls, forwardIconCls } = this,
            { startDate, endDate } = this.client.visibleDateRange;

        return [
            // task is before the view timespan
            record.endDate <= startDate ? {
                class    : 'b-scroll-button-wrap',
                children : [
                    {
                        tag   : 'i',
                        class : {
                            'b-icon'          : 1,
                            'b-scroll-button' : 1,
                            [backwardIconCls] : 1
                        }
                    },
                    labelRenderer && {
                        tag   : 'span',
                        class : 'b-scroll-button-content',
                        text  : this.labelRenderer({ taskRecord : record })
                    }
                ]
            } : null,
            // task is after the view timespan
            record.startDate >= endDate ? {
                class    : 'b-scroll-button-wrap',
                children : [
                    {
                        tag   : 'i',
                        class : {
                            'b-icon'          : 1,
                            'b-scroll-button' : 1,
                            [forwardIconCls]  : 1
                        }
                    },
                    labelRenderer && {
                        tag   : 'span',
                        class : 'b-scroll-button-content',
                        text  : this.labelRenderer({ taskRecord : record })
                    }
                ]
            } : null
        ];
    }

    async onElementClick(domEvent) {
        const
            me                    = this,
            { client }            = me,
            { target }            = domEvent,
            scrollButtonContainer = target.closest('.b-scroll-buttons-container'),
            isScrollButton        = Boolean(scrollButtonContainer);

        if (isScrollButton) {
            const
                taskId     = scrollButtonContainer?.dataset.taskId,
                taskRecord = client.taskStore.getById(taskId);

            /**
             * Fires on owner when the scroll button is clicked, return `false` to prevent default scroll behavior
             * @event scrollButtonClick
             * @preventable
             * @param {Event} domEvent DOM event
             * @param {Gantt.model.TaskModel} taskRecord The task record
             * @on-owner
             */
            if (client.trigger('scrollButtonClick', { domEvent, taskRecord }) !== false) {
                await client.scrollTaskIntoView(taskRecord,
                    Objects.merge({}, me.scrollOptions, {
                        block : target.classList.contains(me.forwardIconCls) ? 'end' : 'start'
                    }), true
                );

                !me.isDestroyed && me.refreshScrollButtons();
            }
        }
    }
}

GridFeatureManager.registerFeature(ScrollButtons, false, 'Gantt');
